import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { numberValidator } from '@shared/validators/number-validator';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, TDeliverable } from '../deliverable-list.interface';
import { ChatbotComponent } from './chatbot.component';
import { IChatbotDeliverableForm } from './chatbot.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly translate = inject(TranslateService);

  public getConfig(): IDeliverableConfig {
    return {
      component: ChatbotComponent,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IChatbotDeliverableForm> | null => {
        if (item && item.type !== EDeliverableType.chatBot) {
          return null;
        }

        const form = new FormGroup({
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? []),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(item?.globalCtaValues ?? null),
          id: new FormControl<string | null>(item?.id ?? uuidv4(), Validators.required),
          interval: new FormControl<string | null>(
            item?.interval ?? null,
            isViewMode ? [] : [Validators.required, numberValidator()],
          ),
          isNew: new FormControl<boolean>(!item?.id),
          name: new FormControl<string | null>(
            item?.name ??
              this.translate.instant('pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotDeliverable.title'),
            [Validators.required, Validators.maxLength(255)],
          ),
          sponsoredContentRelevant: new FormControl<boolean>(item?.sponsoredContentRelevant ?? true, {
            nonNullable: true,
          }),
          text: new FormControl<string | null>(
            item?.text ?? null,
            isViewMode ? [] : [Validators.required, Validators.maxLength(1024)],
          ),
          type: new FormControl<EDeliverableType.chatBot>(EDeliverableType.chatBot, { nonNullable: true }),
        }) as FormGroup<IChatbotDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
